import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Download, Eye, FileText, Edit3, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CreateDocumentDialog from "./CreateDocumentDialog";

const DOC_TYPES = ["ID", "Income Proof", "Tax Return", "Paystub", "Loan Statement", "Authorization Form", "Application", "Other"];
const DOC_STATUSES = [
    { v: "needed", l: "Needed" },
    { v: "requested", l: "Requested" },
    { v: "received", l: "Received" },
    { v: "reviewed", l: "Reviewed" },
    { v: "rejected", l: "Rejected" },
    { v: "not_required", l: "Not required" },
];

const MAX_BYTES = 10 * 1024 * 1024;

function bytesLabel(n) {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function StatusPill({ v }) {
    const map = {
        needed: "bg-amber-100 text-amber-800",
        requested: "bg-amber-100 text-amber-800",
        received: "bg-blue-100 text-blue-800",
        reviewed: "bg-emerald-100 text-emerald-800",
        rejected: "bg-rose-100 text-rose-800",
        not_required: "bg-gray-100 text-gray-600",
    };
    return <span className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded ${map[v] || "bg-gray-100"}`}>{(v || "").replace("_", " ")}</span>;
}

export default function ClientDocumentsTab({ client, onChanged }) {
    const [docs, setDocs] = useState([]);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [preview, setPreview] = useState(null); // { mime, dataUrl, filename }
    const [file, setFile] = useState(null);
    const [docType, setDocType] = useState("Other");
    const [docStatus, setDocStatus] = useState("received");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const [editForm, setEditForm] = useState({ document_name: "", document_type: "Other", document_status: "received", notes: "" });

    const load = useCallback(async () => {
        const r = await api.get(`/clients/${client.id}/documents`);
        setDocs(r.data || []);
    }, [client.id]);

    useEffect(() => { load(); }, [load]);

    const resetUpload = () => {
        setFile(null); setDocType("Other"); setDocStatus("received"); setNotes("");
    };

    const submitUpload = async () => {
        if (!file) return;
        if (file.size > MAX_BYTES) { toast.error("File too large (max 10 MB)"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("document_type", docType);
            fd.append("document_status", docStatus);
            fd.append("notes", notes || "");
            await api.post(`/clients/${client.id}/documents`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Document uploaded");
            setUploadOpen(false);
            resetUpload();
            await load();
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const startEdit = (d) => {
        setEditing(d);
        setEditForm({
            document_name: d.document_name || "",
            document_type: d.document_type || "Other",
            document_status: d.document_status || "received",
            notes: d.notes || "",
        });
    };
    const saveEdit = async () => {
        try {
            await api.patch(`/clients/${client.id}/documents/${editing.id}`, editForm);
            toast.success("Document updated");
            setEditing(null);
            load();
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openPreview = async (d) => {
        try {
            const r = await api.get(`/clients/${client.id}/documents/${d.id}/preview-bytes`);
            const dataUrl = `data:${r.data.mime};base64,${r.data.data_base64}`;
            setPreview({ mime: r.data.mime, dataUrl, filename: r.data.filename || d.document_name });
        } catch (e) {
            const msg = formatApiError(e);
            if (msg.includes("415") || msg.toLowerCase().includes("preview")) {
                toast.message("Preview not available. Download to view.");
            } else toast.error(msg);
        }
    };

    const downloadDoc = async (d) => {
        try {
            const r = await api.get(`/clients/${client.id}/documents/${d.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = d.document_name || d.original_filename || "document";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const previewable = (mime) => (mime || "").startsWith("image/") || (mime || "") === "application/pdf";

    return (
        <div className="space-y-4" data-testid="client-documents-tab">
            <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="text-sm font-medium text-gray-900">Documents</div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)} data-testid="create-doc-button">
                            <Sparkles className="h-4 w-4 mr-1" /> Create Document
                        </Button>
                        <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="upload-doc-button">
                            <Upload className="h-4 w-4 mr-1" /> Upload Document
                        </Button>
                    </div>
                </div>

                {docs.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        <div className="text-sm font-medium text-gray-700">No documents yet.</div>
                        <div className="text-xs mt-1">Upload IDs, paystubs, tax returns, or any other supporting docs.</div>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                            <tr>
                                <th className="py-2">Name</th>
                                <th className="py-2">Type</th>
                                <th className="py-2">Status</th>
                                <th className="py-2">Size</th>
                                <th className="py-2">Uploaded</th>
                                <th className="py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.map(d => (
                                <tr key={d.id} className="border-t border-border" data-testid={`doc-row-${d.id}`}>
                                    <td className="py-2.5">
                                        <div className="text-sm text-gray-900">{d.document_name}</div>
                                        {d.notes && <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[280px]">{d.notes}</div>}
                                    </td>
                                    <td className="py-2.5 text-xs">{d.document_type}</td>
                                    <td className="py-2.5"><StatusPill v={d.document_status} /></td>
                                    <td className="py-2.5 text-xs text-muted-foreground">{bytesLabel(d.file_size)}</td>
                                    <td className="py-2.5 text-xs text-muted-foreground">
                                        {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"}
                                        <div>{d.uploaded_by_name || ""}</div>
                                    </td>
                                    <td className="py-2.5 text-right">
                                        <div className="inline-flex gap-1">
                                            {previewable(d.file_mime_type) && (
                                                <Button size="sm" variant="ghost" onClick={() => openPreview(d)} data-testid={`preview-doc-${d.id}`}><Eye className="h-4 w-4" /></Button>
                                            )}
                                            <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} data-testid={`download-doc-${d.id}`}><Download className="h-4 w-4" /></Button>
                                            <Button size="sm" variant="ghost" onClick={() => startEdit(d)} data-testid={`edit-doc-${d.id}`}><Edit3 className="h-4 w-4" /></Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Upload dialog */}
            <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) resetUpload(); }}>
                <DialogContent className="sm:max-w-[480px]" data-testid="upload-doc-dialog">
                    <DialogHeader>
                        <DialogTitle>Upload Document</DialogTitle>
                        <DialogDescription>Files are stored privately in MongoDB and require auth to download. Max 10 MB.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">File</label>
                            <Input type="file" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
                            {file && <div className="text-xs text-muted-foreground">{file.name} · {bytesLabel(file.size)}</div>}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Type</label>
                                <Select value={docType} onValueChange={setDocType}>
                                    <SelectTrigger data-testid="upload-doc-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Status</label>
                                <Select value={docStatus} onValueChange={setDocStatus}>
                                    <SelectTrigger data-testid="upload-doc-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>{DOC_STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Notes (optional)</label>
                            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="upload-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={busy}>Cancel</Button>
                        <Button onClick={submitUpload} disabled={!file || busy} data-testid="upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
                <DialogContent className="sm:max-w-[480px]" data-testid="edit-doc-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit Document</DialogTitle>
                        <DialogDescription>Update name, type, status, or notes. Changing status writes a history event.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Name</label>
                            <Input value={editForm.document_name} onChange={e => setEditForm({ ...editForm, document_name: e.target.value })} data-testid="edit-doc-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Type</label>
                                <Select value={editForm.document_type} onValueChange={v => setEditForm({ ...editForm, document_type: v })}>
                                    <SelectTrigger data-testid="edit-doc-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Status</label>
                                <Select value={editForm.document_status} onValueChange={v => setEditForm({ ...editForm, document_status: v })}>
                                    <SelectTrigger data-testid="edit-doc-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>{DOC_STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Notes</label>
                            <Textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows={3} data-testid="edit-doc-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={saveEdit} data-testid="edit-doc-save">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview dialog */}
            <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
                <DialogContent className="sm:max-w-[900px]" data-testid="preview-doc-dialog">
                    <DialogHeader>
                        <DialogTitle className="truncate">{preview?.filename}</DialogTitle>
                        <DialogDescription>Preview rendered from a short-lived in-memory data URL.</DialogDescription>
                    </DialogHeader>
                    <div className="w-full h-[70vh] bg-gray-50 rounded border border-border overflow-hidden">
                        {preview?.mime?.startsWith("image/")
                            ? <img src={preview.dataUrl} alt="" className="w-full h-full object-contain" />
                            : <iframe src={preview?.dataUrl} title="preview" className="w-full h-full" />}
                    </div>
                </DialogContent>
            </Dialog>

            <CreateDocumentDialog open={createOpen} onOpenChange={setCreateOpen} client={client} onGenerated={() => { load(); onChanged && onChanged(); }} />
        </div>
    );
}
